import { useState } from "react";
import SinglePaymentAccordionItem from "../SinglePaymentSelection/SinglePaymentAccordionItem";
import { usePayment } from "../Checkout/context/PaymentContext";
import SinglePaymentCreditCard from "../SinglePaymentSelection/SinglePaymentOptions/SinglePaymentCreditCard";
import SinglePaymentPayPal from "../SinglePaymentSelection/SinglePaymentOptions/SinglePaymentPayPal";
import SinglePaymentTransfer from "../SinglePaymentSelection/SinglePaymentOptions/SinglePaymentTransfer";
import SinglePaymentCreditCombined from "../SinglePaymentSelection/SinglePaymentOptions/SinglePaymentCreditCombined";
import localeText from "../../../../locale";

const MultiPaymentSelection = ({ toast, totalPrice, combinePayment }) => {
  const {
    extracoinInWallet,
    currencyProduct,
    currencyFormat,
    walletBalanceValue,
    isTotalCovered,
    isPaymentMethodInUse,
    isPaymentMethodAvailable,
    setSelectedPaymentMethod
  } = usePayment();

  const payments = [
    { id: 1, title: localeText("payWithExtracreditAndWallet"), type: "creditCombined" },
    { id: 2, title: localeText("payWithBankTransfer"), type: "bankTransfer" },
    { id: 3, title: localeText("payWithCreditCard"), type: "creditCard" },
    { id: 4, title: localeText("payWithPayPal"), type: "paypal" },
  ];

  const [openItemIds, setOpenItemIds] = useState([]);

  const isPaymentDisabled = (id) => {
    if (!combinePayment) return false;

    const creditCardAvailable = isPaymentMethodAvailable("creditCard");
    const paypalAvailable = isPaymentMethodAvailable("paypal");

    switch (id) {
      case 1: // Extracredit + Wallet
        return !isPaymentMethodAvailable("extracredit") && !isPaymentMethodAvailable("wallet");
      case 2: // Bank Transfer
        return !isPaymentMethodAvailable("bankTransfer");
      case 3: // Credit Card
        return !creditCardAvailable;
      case 4: // PayPal
        return !paypalAvailable;
      default:
        return false;
    }
  };

  const toggleItem = (id) => {
    if (isPaymentDisabled(id)) return;

    const paymentType = payments.find(p => p.id === id)?.type;
    const isCurrentlyOpen = openItemIds.includes(id);

    if (isCurrentlyOpen && (paymentType === "creditCard" || paymentType === "paypal")) {
      setSelectedPaymentMethod(null);
    }
    else if (!isCurrentlyOpen && (paymentType === "creditCard" || paymentType === "paypal")) {
      setSelectedPaymentMethod(paymentType);
    }

    const totalCovered = isTotalCovered();
    if (totalCovered) {
      let canOpen = false;
      switch (id) {
        case 1: // Extracredit + Wallet
          canOpen = isPaymentMethodInUse('extracredit') || isPaymentMethodInUse('wallet');
          break;
        case 2: // Bank Transfer
          canOpen = isPaymentMethodInUse('bankTransfer');
          break;
        case 3: // Credit Card
          canOpen = isPaymentMethodInUse('creditCard');
          break;
        case 4: // PayPal
          canOpen = isPaymentMethodInUse('paypal');
          break;
        default:
          canOpen = false;
      }
      if (!canOpen) {
        return;
      }
    }

    setOpenItemIds((prevOpenItemIds) =>
      prevOpenItemIds.includes(id)
        ? prevOpenItemIds.filter((itemId) => itemId !== id)
        : [...prevOpenItemIds, id]
    );
  };

  function getPaymentType(id) {
    switch (id) {
      case 1:
        return (
          <SinglePaymentCreditCombined
            currencyProduct={currencyProduct}
            currencyFormat={currencyFormat}
            extracoinInWallet={extracoinInWallet}
            walletBalanceValue={walletBalanceValue}
            combinePayment={combinePayment}
            totalPrice={totalPrice}
          />
        );
      case 2:
        return (
          <SinglePaymentTransfer
            currencyProduct={currencyProduct}
            currencyFormat={currencyFormat}
            toast={toast}
            combinePayment={combinePayment}
            totalPrice={totalPrice}
          />
        );
      case 3:
        return <SinglePaymentCreditCard combinePayment={combinePayment} totalPrice={totalPrice} />;
      case 4:
        return <SinglePaymentPayPal combinePayment={combinePayment} totalPrice={totalPrice} />;
      default:
        return null;
    }
  }

  return (
    <div className="w-full mx-auto">
      {payments.map((payment) => {
        const isDisabled = isPaymentDisabled(payment.id);

        return (
          <SinglePaymentAccordionItem
            key={payment.id}
            title={payment.title}
            isOpen={openItemIds.includes(payment.id)}
            onToggle={() => toggleItem(payment.id)}
            id={payment.id}
            toast={toast}
            getPaymentType={getPaymentType}
            combinePayment={combinePayment}
            totalPrice={totalPrice}
            disabled={isDisabled}
          />
        );
      })}
    </div>
  );
};

export default MultiPaymentSelection;
